import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import PropTypes from 'prop-types';
import { useGoogleOAuth } from './google-oauth-context.js';

var DEFAULT_SCOPES = "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/user.phonenumbers.read https://www.googleapis.com/auth/user.addresses.read https://www.googleapis.com/auth/user.birthday.read https://www.googleapis.com/auth/user.gender.read";
/**
 * 
 * @name useGoogleLogin
 * 
 *  Custom hook to handle Google login functionality. 
 *  Requires to be wrapped in GoogleOAuthProvider.
 * 
 * @param {Object} props - Props for configuring Google login.
 * @param {string} props.scope - The scope of the access request.
 * @param {string} props.flow - The flow of authentication: 'implicit' or 'authCode'.
 * @param {string} props.state - An opaque value used by the client to maintain state between the request and callback.
 * @param {boolean} props.overrideScope - Whether to override the default scope.
 * @param {Function} props.onNonOAuthError - Callback function to handle non-OAuth errors.
 * @param {Function} props.onError - Callback function to handle OAuth errors.
 * @param {Function} props.onSuccess - Callback function to handle successful login.
 * @returns {Function} - Function to trigger the login process.
 * 
 * @example
    const handleGoogleLogin = useGoogleLogin({
    onSuccess: tokenResponse => handleGoogleLoginSuccess(tokenResponse),
    onError: error =>
      dispatch(
        Notifications.add({
          level: 'error',
          message: error,
        })
      ),
    scope: 'profile email address phone',
    enable_serial_consent: true,
    ux_mode: 'popup',
    flow: 'implicit',
  })
 */

function useGoogleLogin(props) {
  // Refs to hold the latest values of onSuccess, onError, and onNonOAuthError callbacks
  var {
    scope,
    state,
    overrideScope,
    onNonOAuthError,
    onError,
    onSuccess
  } = props;
  var {
    clientId,
    scriptLoadedSuccessfully
  } = useGoogleOAuth();
  var clientRef = React.useRef();
  var onSuccessRef = React.useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  var onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;
  var onNonOAuthErrorRef = React.useRef(onNonOAuthError);
  onNonOAuthErrorRef.current = onNonOAuthError;
  React.useEffect(() => {
    if (!scriptLoadedSuccessfully) {
      return;
    } // Initialize the token client


    var client = window.google.accounts.oauth2["initTokenClient"](_objectSpread2({
      client_id: clientId,
      prompt: "consent select_account",
      include_granted_scopes: true,
      responseType: "permission token",
      scope: overrideScope ? scope : "".concat(DEFAULT_SCOPES, " ").concat(scope),
      callback: response => {
        var _onSuccessRef$current;

        if (response.error) {
          var _onErrorRef$current;

          return (_onErrorRef$current = onErrorRef.current) === null || _onErrorRef$current === void 0 ? void 0 : _onErrorRef$current.call(onErrorRef, response);
        }

        (_onSuccessRef$current = onSuccessRef.current) === null || _onSuccessRef$current === void 0 ? void 0 : _onSuccessRef$current.call(onSuccessRef, response);
      },
      error_callback: nonOAuthError => {
        var _onNonOAuthErrorRef$c;

        (_onNonOAuthErrorRef$c = onNonOAuthErrorRef.current) === null || _onNonOAuthErrorRef$c === void 0 ? void 0 : _onNonOAuthErrorRef$c.call(onNonOAuthErrorRef, nonOAuthError);
      },
      state
    }, props));
    clientRef.current = client;
  }, [clientId, scriptLoadedSuccessfully, scope, state]);
  /**
   *
   *  @name loginImplicitFlow
   *
   *  Method "loginImplicitFlow" can be used to trigger the implicit flow
   *  of the Google login process. It will request an access token from Google
   *  and execute the onSuccess callback if successful, otherwise onError callback
   *  will be executed.
   *
   * @access public
   * */

  var loginImplicitFlow = React.useCallback(overrideConfig => {
    var _clientRef$current;

    return (_clientRef$current = clientRef.current) === null || _clientRef$current === void 0 ? void 0 : _clientRef$current.requestAccessToken(overrideConfig);
  }, []);
  return loginImplicitFlow;
}
useGoogleLogin.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onNonOAuthError: PropTypes.func,
  scope: PropTypes.string,
  state: PropTypes.string,
  overrideScope: PropTypes.bool,
  ux_mode: PropTypes.string
};

export { useGoogleLogin };
